/**
 * Detects blank or uniform images (e.g. solid white/black, empty captures).
 * Returns true when the image has no meaningful visual content.
 */
const MIN_DIMENSION = 10;
const LUMINANCE_VARIANCE_THRESHOLD = 25;
const MIN_VARIED_PIXEL_RATIO = 0.01;
const PIXEL_DIFF_THRESHOLD = 12;

function getLuminance(r, g, b) {
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

export function isBlankImage(dataUrl) {
    return new Promise((resolve) => {
        if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
            resolve(true);
            return;
        }

        const img = new Image();
        img.onload = () => {
            try {
                if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
                    resolve(true);
                    return;
                }

                const sampleSize = 64;
                const width = Math.min(img.width, sampleSize);
                const height = Math.min(img.height, sampleSize);

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    resolve(true);
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);
                const { data } = ctx.getImageData(0, 0, width, height);

                if (!data || data.length < 4) {
                    resolve(true);
                    return;
                }

                const luminances = [];
                for (let i = 0; i < data.length; i += 4) {
                    luminances.push(getLuminance(data[i], data[i + 1], data[i + 2]));
                }

                const mean = luminances.reduce((sum, value) => sum + value, 0) / luminances.length;
                const variance =
                    luminances.reduce((sum, value) => sum + (value - mean) ** 2, 0) / luminances.length;

                const variedPixels = luminances.filter(
                    (value) => Math.abs(value - mean) > PIXEL_DIFF_THRESHOLD
                ).length;
                const variedRatio = variedPixels / luminances.length;

                resolve(
                    variance < LUMINANCE_VARIANCE_THRESHOLD || variedRatio < MIN_VARIED_PIXEL_RATIO
                );
            } catch {
                // Canvas ops can throw (e.g. SecurityError); treat as invalid so callers never hang.
                resolve(true);
            }
        };

        img.onerror = () => resolve(true);
        img.src = dataUrl;
    });
}

export const INVALID_IMAGE_MESSAGE =
    "Please upload a valid image. The photo appears to be blank or invalid.";
